import { useState, useEffect, useRef, useCallback } from "react";
import api from '../services/api';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import "react-datepicker/dist/react-datepicker.css";
import { DateBox } from "devextreme-react/date-box";
import "bootstrap/dist/css/bootstrap.min.css";
import { Tab, Nav } from "react-bootstrap";
import CollapsiblePanel from "../components/CollapsiblePanel";
import countries from "../data/countries";
import { useAgents } from "../context/AgentContext";
import { useCompany } from "../context/CompanyContext";
import { createApplicant, updateApplicant, uploadFile } from "../services/applicantService";
import { useNavigate } from "react-router-dom";
import { LoadPanel } from "devextreme-react/load-panel";
import { FaSave, FaExternalLinkAlt } from "react-icons/fa";

// Validation schema
const schema = (applicant, minAge, passportNoLen, enjazNoLen, visaNoLen, sponsorIdLen) =>
  yup.object().shape({
    fullName: yup.string().required("Required").max(120, "Exceed [120]"),
    dateOfBirth: yup
      .date()
      .required("Required")
      .test("min-age", `Under Age`, (value) => {
        if (!value) return false;
        const today = new Date();
        const age = today.getFullYear() - value.getFullYear();
        const month = today.getMonth() - value.getMonth();
        if (month < 0 || (month === 0 && today.getDate() < value.getDate())) {
          return age - 1 >= minAge;
        }
        return age >= minAge;
      }),
    placeOfBirth: yup.string().required("Required").max(45, "Exceed [45]"),
    passportNo: yup.string().required("Required").max(20, "Exceed [20]").test(
      "passport-length",
      `Passport number must be exactly ${passportNoLen} characters`,
      (value) => {
        if (!value || !passportNoLen || passportNoLen === 0) return true;
        return value && value.length === passportNoLen;
      }
    ),
    passportType: yup.string().required("Required"),
    placeOfIssue: yup.string().required("Required"),
    pasportIssuedDate: yup.date().required("Required"),
    pasportExpiryDate: yup.date().required("Required"),
    religion: yup.string().required("Required"),
    maritalStatus: yup.string().required("Required"),
    gender: yup.string().required("Required"),    
    occupation: yup.string().required("Required").max(40, "Exceed [40]"),
    sponsorName: yup.string().max(120, "Exceed [120]"),
    sponsorPhone: yup.string().max(20, "Exceed [20]"),
    sponsorAddress: yup.string().max(80, "Exceed [80]"),
    phoneNo: yup.string().max(20, "Exceed [20]"),
    applicationNo: yup.string().max(20, "Exceed [20]").test(
      "enjaz-length",
      `[${enjazNoLen}]`,
      (value) => {
        if (!value || !enjazNoLen || enjazNoLen === 0) return true;
        return value && value.length === enjazNoLen;
      }
    ),
    visaNo: yup.string().max(20, "Exceed [20]").test(
      "visa-length",
      `(${visaNoLen})`,
      (value) => {
        if (!value || !visaNoLen || visaNoLen === 0) return true;
        return value && value.length === visaNoLen;
      }
    ),
    sponsorID: yup.string().max(20, "Exceed [20]").test(
      "sponsorId-length",
      `(${sponsorIdLen})`,
      (value) => {
        if (!value || !sponsorIdLen || sponsorIdLen === 0) return true;
        return value && value.length === sponsorIdLen;
      }
    ),
    photoFile: yup
      .mixed()
      .test("required", "Photo is required", (value, context) => {
        if (applicant && applicant.photoKey) return true;
        return value && value.length > 0;
      })
      .test("fileType", "Only JPG, JPEG, and PNG are allowed", (value, context) => {
        if (applicant && applicant.photoKey) return true;
        return (
          value &&
          value.length > 0 &&
          ["image/jpeg", "image/png", "image/jpg"].includes(value[0].type)
        );
      })
      .test("fileSize", "File size must be less than 2MB", (value, context) => {
        if (applicant && applicant.photoKey) return true;
        return value && value.length > 0 && value[0].size <= 2 * 1024 * 1024;
      }),

      interviewFile: yup
      .mixed()
      .test("fileType", "Only MP4, AVI, MOV, and WMV video files are allowed", (value, context) => {
        // If no file is selected, it's valid (optional field)
        if (!value || value.length === 0) {
          return true;
        }
        // Validate the file type
        return ["video/mp4", "video/avi", "video/quicktime", "video/x-ms-wmv"].includes(value[0].type);
      })
      .test("fileSize", "Video file size must be less than 10MB", (value, context) => {
        // If no file is selected, it's valid (optional field)
        if (!value || value.length === 0) {
          return true;
        }
        // Validate the file size
        return value[0].size <= 10 * 1024 * 1024;
      }),
  });

const previewBoxStyle = {
  width: "175px",
  height: "175px",
  border: "1px solid #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  marginBottom: "8px",
};

const imageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
};

const ApplicantForm = ({ applicant, applicantId, onSuccess }) => {
  const agents = useAgents() || [];
  const company = useCompany();
  const navigate = useNavigate();
  const title = applicant ? "EDIT APPLICANT" : "CREATE APPLICANT";
  const companyIsActive = company?.is_Active ?? false;
  const companyIsPremium = company?.isPremium ?? false;
  const allowVideo = company!=null && company.videoExpDate!=null && new Date(company.videoExpDate) > new Date(Date.now());
  const toBeExpired = company?.licenseDate ? new Date(company.licenseDate) < new Date(Date.now() + 16 * 24 * 60 * 60 * 1000) : false;

  const [userSettings, setUserSettings] = useState({
    minAge: 0,
    passportNoLength: 0,
    enjazNoLength: 0,
    visaNoLength: 0,
  });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("photoFile");
  const [imageLoading, setImageLoading] = useState({});
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [isScanningMrz, setIsScanningMrz] = useState(false);
  const [mrzError, setMrzError] = useState(null);
  const [mrzSuccess, setMrzSuccess] = useState(false);
  const photoFileRef = useRef(null);

  const [previews, setPreviews] = useState({
    photoFile: null,
    fullSizeFile: null,
    passportFile: null,
    interviewFile: null,
    cocFile: null,
    medicalFile: null,
    idCardFile: null,
    relativeIdFile: null,
    insuranceFile: null,
    regFormFile: null,
  });

  // Fetch user settings on component mount
  useEffect(() => {
    api.get("/DefaultSettings")
      .then((response) => {
        setUserSettings(response.data);
      })
      .catch((error) => console.error("Error fetching default settings:", error))
      .finally(() => setLoading(false));
  }, []);

  // Initialize form with userSettings
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      schema(
        applicant,
        userSettings.minAge,
        userSettings.passportNoLength,
        userSettings.enjazNoLength,
        userSettings.visaNoLength,
        userSettings.sponsorIDLength,
      )
    ),
  });

  // Function to get file URL from S3
  const getFileUrl = async (fileKey) => {
    if (!fileKey) return null;
    
    try {
      // If it's already a full URL (legacy data), extract the key
      let actualFileKey = fileKey;
      
      if (fileKey.startsWith('http')) {
        // Extract key from full URL
        const url = new URL(fileKey);
        actualFileKey = url.pathname.substring(1); // Remove leading slash
      }
      const response = await api.get(`/applicants/file-url?fileKey=${encodeURIComponent(actualFileKey)}`);
      
      return response.data.url;
    } catch (error) {
      return null;
    }
  };

  // Function to get public URL for video files
  const getPublicUrl = async (fileKey) => {
    if (!fileKey) return null;
    
    try {
      // If it's already a full URL (legacy data), extract the key
      let actualFileKey = fileKey;
      
      if (fileKey.startsWith('http')) {
        // Extract key from full URL
        const url = new URL(fileKey);
        actualFileKey = url.pathname.substring(1); // Remove leading slash
      }
      const response = await api.get(`/applicants/public-url?fileKey=${encodeURIComponent(actualFileKey)}`);
      
      return response.data.url;
    } catch (error) {
      return null;
    }
  };

  // Function to convert data URL to Blob
const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

// Function to scan MRZ from uploaded file
const handleScanMrzFromUploaded = async () => {
  if (!previews.passportFile) {
    setMrzError('No passport image uploaded. Please upload a passport image first.');
    return;
  }

  setIsScanningMrz(true);
  setMrzError(null);
  setMrzSuccess(false);

  try {
    // Convert the preview URL to a Blob
    const response = await fetch(previews.passportFile);
    const blob = await response.blob();
    
    // Create a File object from the Blob
    const file = new File([blob], 'passport.jpg', { type: 'image/jpeg' });

    const formData = new FormData();
    formData.append('passportImage', file);

    const apiResponse = await api.post('/applicants/scan-mrz', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const mrzData = apiResponse.data;

    if (mrzData.success) {
      // Attempt to auto-fill only if at least 5 fields are valid
      const wasAutoFilled = await autoFillFormWithMrzData(mrzData);
      
      if (wasAutoFilled) {
        // Count valid fields to determine message
        let validCounter = 0;
        if (mrzData.surnameValid) validCounter++;
        if (mrzData.givenNamesValid) validCounter++;
        if (mrzData.passportNumberValid) validCounter++;
        if (mrzData.sexValid) validCounter++;
        if (mrzData.dateOfBirthValid) validCounter++;
        if (mrzData.dateOfExpiryValid) validCounter++;

        if (validCounter === 6) {
          // All 6 fields are valid - show success message
          setMrzSuccess(true);
          setMrzError(null);
          // If you want to show a success message in green, you might need a success state
          // For now, we'll just not show any error
        } else if (validCounter === 5) {
          // Only 5 fields valid - show warning message
          setMrzSuccess(false);
          setMrzError('Some fields may need verification. Please review the auto-filled data.');
        }
      } else {
        // Less than 5 fields valid - show error message
        setMrzSuccess(false);
        setMrzError('Please upload better quality image which can be scanned clearly!');
      }
    } else {
      setMrzSuccess(false);
      setMrzError('Could not read MRZ data from the uploaded image. Please ensure the MRZ zone (bottom 2 lines) is clearly visible and try again.');
    }
  } catch (error) {
    console.error('Error scanning MRZ:', error);
    setMrzSuccess(false);
    setMrzError('Error scanning MRZ. Please try again or enter manually.');
  } finally {
    setIsScanningMrz(false);
  }
};

// Helper function to subtract days from a date string (yyyy-MM-dd format)
const subtractDaysFromDate = (dateString, daysToSubtract) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return null;
    }
    
    // Subtract the days
    date.setDate(date.getDate() - daysToSubtract);
    
    // Format back to yyyy-MM-dd
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error calculating issued date:', error);
    return null;
  }
};

const autoFillFormWithMrzData = async (mrzData) => {  
  // Count valid fields
  let validCounter = 0;
  if (mrzData.surnameValid) validCounter = validCounter + 1;
  if (mrzData.givenNamesValid) validCounter = validCounter + 1;
  if (mrzData.passportNumberValid) validCounter = validCounter + 1;
  if (mrzData.sexValid) validCounter = validCounter + 1;
  if (mrzData.dateOfBirthValid) validCounter = validCounter + 1;
  if (mrzData.dateOfExpiryValid) validCounter = validCounter + 1;

  // Only set values if at least 5 fields are valid
  if (validCounter >= 5) {
    // Set values for each field individually if they are valid
    if (mrzData.surnameValid && mrzData.givenNamesValid && mrzData.surname && mrzData.givenNames && mrzData.givenNames.trim().includes(" ")) {
      setValue("fullName", `${mrzData.givenNames} ${mrzData.surname}`.trim());
    }

    if (mrzData.passportNumberValid && mrzData.passportNumber) {
      setValue("passportNo", mrzData.passportNumber);
    }

    if (mrzData.sexValid && mrzData.sex) {
      const genderValue = mrzData.sex === 'M' ? 'Male' : mrzData.sex === 'F' ? 'Female' : '';
      setValue("gender", genderValue);
    }

    if (mrzData.dateOfBirthValid && mrzData.dateOfBirth) {
      setValue("dateOfBirth", mrzData.dateOfBirth);
    }

    //if (mrzData.dateOfExpiryValid && mrzData.dateOfExpiry) {
    //  setValue("pasportExpiryDate", mrzData.dateOfExpiry);
    //}
    if (mrzData.dateOfExpiryValid && mrzData.dateOfExpiry) {
      setValue("pasportExpiryDate", mrzData.dateOfExpiry);
  
  // Calculate passport issued date based on passport number pattern
  if (mrzData.passportNumber && mrzData.passportNumber.length >= 2) {
        const firstTwoChars = mrzData.passportNumber.substring(0, 2).toUpperCase();
        const firstChar = mrzData.passportNumber.substring(0, 1).toUpperCase();
        const secondChar = mrzData.passportNumber.substring(1, 2);
        
        let issuedDate = null;

        const birthDate = new Date(mrzData.dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        // Adjust age if birthday hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        // Check if passport number starts with EP or EQ
        if (firstTwoChars === 'EP' || firstTwoChars === 'EQ' || age <= 25) {
          // Subtract 1825 days (5 years) from expiry date
          issuedDate = subtractDaysFromDate(mrzData.dateOfExpiry, 1825);
        } 
        // Check if passport number starts with E and second character is a number
        else if (firstChar === 'E' && !isNaN(secondChar) && secondChar !== ' ') {
          // Subtract 3650 days (10 years) from expiry date
          issuedDate = subtractDaysFromDate(mrzData.dateOfExpiry, 3651);
        }
        
        // Set the issued date if calculated
        if (issuedDate) {
          setValue("pasportIssuedDate", issuedDate);
          console.log('✓ Set pasportIssuedDate:', issuedDate, 'based on passport number:', mrzData.passportNumber);
        }
      }
    }

    if (mrzData.issuingCountry) {
    // Get current value of placeOfIssue
    const currentPlaceOfIssue = getValues("placeOfIssue");
    
    // If placeOfIssue is empty or equals "ETH", set it to "Ethiopia"
      if ((!currentPlaceOfIssue || currentPlaceOfIssue === "") && mrzData.issuingCountry === "ETH") {
        setValue("placeOfIssue", "Ethiopia");
      } 
    }

    // Count how many fields were successfully set
    const setFields = [
      mrzData.surnameValid && mrzData.givenNamesValid ? 'fullName' : null,
      mrzData.passportNumberValid ? 'passportNo' : null,
      mrzData.sexValid ? 'gender' : null,
      mrzData.dateOfBirthValid ? 'dateOfBirth' : null,
      mrzData.dateOfExpiryValid ? 'pasportExpiryDate' : null
    ].filter(Boolean);

    return true; // Indicate that auto-fill was performed
  } else {
    return false; // Indicate that auto-fill was NOT performed
  }
};

// Optional: Show data preview without auto-filling
const showMrzDataPreview = (mrzData) => {
  const previewText = `
    Scanned Data (Low Confidence - Please Verify):
    Name: ${mrzData.surname} ${mrzData.givenNames}
    Passport: ${mrzData.passportNumber}
    Nationality: ${mrzData.nationality}
    DOB: ${mrzData.dateOfBirth}
    Expiry: ${mrzData.dateOfExpiry}
    
    Issues detected: ${mrzData.validationErrors?.join(', ')}
  `;
  
  // You could show this in a modal or dedicated preview area
  console.log(previewText);
  // setMrzPreviewData(mrzData); // If you want to show in UI
};

  // Function to upload file to S3
  const uploadFileToS3 = async (file, fileType, applicantId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      formData.append('applicantId', applicantId);

      // Use the same endpoint for both images and videos
      const response = await api.post('/applicants/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.fileKey;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Revoke all object URLs to prevent memory leaks
      Object.values(previews).forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // Load file previews - memoized to prevent unnecessary re-renders
  const loadFilePreviews = useCallback(async () => {
    if (!applicant || filesLoaded) return;

    const previewsData = { ...previews };
    
    // Create an array of promises for all file URL requests
    const fileKeys = [
      { field: 'photoFile', key: applicant.photoKey },
      { field: 'fullSizeFile', key: applicant.fullSizeKey },
      { field: 'passportFile', key: applicant.passportKey },
      { field: 'interviewFile', key: applicant.interviewKey },
      { field: 'cocFile', key: applicant.doc1Key },
      { field: 'medicalFile', key: applicant.doc2Key },
      { field: 'idCardFile', key: applicant.idCardKey },
      { field: 'relativeIdFile', key: applicant.relativeIDKey },
      { field: 'insuranceFile', key: applicant.insuranceKey },
      { field: 'regFormFile', key: applicant.regFormKey }
    ];
    
    // Set loading state only for files that exist
    const loadingStates = {};
    fileKeys.forEach(({ field, key }) => {
      if (key) {
        loadingStates[field] = true;
      }
    });
    setImageLoading(loadingStates);
    
    try {
      // Fetch all file URLs in parallel
      const results = await Promise.allSettled(
        fileKeys.map(async ({ field, key }) => {
          if (key) {
            let url;
            if (field === "interviewFile") {
              url = await getPublicUrl(key);
            } else {
              url = await getFileUrl(key);
            }
            return { field, url };
          }
          return { field, url: null };
        })
      );
      
      // Process results
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value.url) {
          previewsData[result.value.field] = result.value.url;
        }
      });
      
      setPreviews(previewsData);
      setFilesLoaded(true);
    } catch (error) {
      console.error('Error loading file previews:', error);
    } finally {
      // Clear loading states
      const clearedLoadingStates = {};
      fileKeys.forEach(({ field }) => {
        clearedLoadingStates[field] = false;
      });
      setImageLoading(clearedLoadingStates);
    }
  }, [applicant, filesLoaded]);

  // Set form values and previews when the applicant prop changes (for editing)
  useEffect(() => {
    if (!applicant && userSettings && Object.keys(userSettings).length > 0) {
        setValue("applicationDate", new Date());
        setValue("passportType", "Normal");
        setValue("currentNationality", userSettings.currentNationality);
        setValue("pastNationality", userSettings.pastNationality);
        setValue("placeOfIssue", userSettings.placeOfIssue);
        setValue("gender", userSettings.gender);
        setValue("salary", userSettings.femaleSalary);
        setValue("religion", userSettings.religion);
        setValue("maritalStatus", userSettings.maritalStatus);
        setValue("qualification", userSettings.qualification);
        setValue("occupation", userSettings.femaleOccupation);
        setValue("visaType", userSettings.visaType);
        setValue("sponsorAddress", userSettings.sponsorAddress);
        setValue("officeAbroad", userSettings.officeAbroad);
        setValue("is_Active", true);
    }    
    
    if (applicant && !filesLoaded) {
      Object.keys(applicant).forEach((key) => {
        setValue(key, applicant[key]);
      });

      loadFilePreviews();
    }

    const handlePaste = (event) => {
      // Get the clipboard data from the event
      const pastedContent = event.clipboardData.getData('text');
      
      if (pastedContent.toUpperCase().includes("LOCATION") && 
            pastedContent.toUpperCase().includes("VISA NUMBER") && 
            pastedContent.toUpperCase().includes("MOBILE NUMBER")) {
            
            let _content = pastedContent.toUpperCase().trim();

            if (_content.includes("EMPLOYMENT DETAILS")) {
                _content = _content.substring(0, _content.indexOf("EMPLOYMENT DETAILS"));
            }
            if (_content.includes("Employment details")) {
                _content = _content.substring(0, _content.indexOf("Employment details"));
            }

            let tmp = "";
            let index = _content.indexOf("LOCATION");
            tmp = _content.substring(0, index).trim();
            _content = _content.substring(_content.indexOf("LOCATION")).trim().replace("LOCATION", "");
            setValue("sponsorName",tmp);

            index = _content.indexOf("VISA NUMBER");
            tmp = _content.substring(0, index).trim();
            setValue("sponsorAddress",tmp);
            _content = _content.substring(_content.indexOf("VISA NUMBER")).trim().replace("VISA NUMBER", "");

            index = _content.indexOf("MOBILE NUMBER");
            tmp = _content.substring(0, index).trim();
            setValue("visaNo",tmp);
            _content = _content.substring(_content.indexOf("MOBILE NUMBER")).replace("MOBILE NUMBER", "").trim();

            if (!_content.includes("CANDIDATE INFO")) {
                setValue("sponsorPhone",_content);
            } else {
                index = _content.indexOf("CANDIDATE INFO");
                tmp = _content.substring(0, index).trim();
                setValue("sponsorPhone",tmp);
            }

            index = _content.indexOf("SURNAME");
            _content = _content.substring(index).trim();
            _content = _content.replace("SURNAME", "").trim();

            index = _content.indexOf("GIVEN NAMES");
            tmp = _content.substring(0, index).trim();
            _content = _content.substring(index).replace("GIVEN NAMES", "").trim();

            index = _content.indexOf("DATE OF BIRTH");
            setValue("fullName",(_content.substring(0, index).trim() + " " + tmp));
            _content = _content.substring(index).replace("DATE OF BIRTH", "").trim();
            
            index = _content.indexOf("GENDER");
            tmp = _content.substring(0, index).trim();
            setValue("dateOfBirth",new Date(tmp));
            _content = _content.substring(index).replace("GENDER", "").trim();
            index = _content.indexOf("MARITAL STATUS");
            tmp = _content.substring(0, index).trim();
            setValue("gender",tmp.toUpperCase()==="MALE"?"Male":"Female");
            _content = _content.substring(index).replace("MARITAL STATUS", "").trim();
            
            index = _content.indexOf("RELIGION");
            tmp = _content.substring(0, index).trim();
            setValue("maritalStatus",tmp.charAt(0) + tmp.slice(1).toLowerCase());
            _content = _content.substring(index).replace("RELIGION", "").trim();
          
            index = _content.indexOf("JOB");
            tmp = _content.substring(0, index).trim();
            setValue("religion",(tmp.toLocaleUpperCase() === "NON MUSLIM" ? "Christian" : "Islam"));
            _content = _content.substring(index).replace("JOB", "").trim();
          
            index = _content.indexOf("EDUCATION LEVEL");
            tmp = _content.substring(0, index).trim();
            setValue("occupation",tmp.toLocaleUpperCase());
            _content = _content.substring(index).replace("EDUCATION LEVEL", "").trim();
            
            index = _content.indexOf("ID NUMBER");
            tmp = _content.substring(0, index).trim();
            setValue("qualification",tmp);
            _content = _content.substring(index).replace("ID NUMBER", "").trim();

            
            _content = _content.replace("MOBILE NUMBER", "PHONE NUMBER");
            _content = _content.substring(_content.indexOf("PHONE NUMBER")).replace("PHONE NUMBER", "").trim();
            index = _content.indexOf("VISA NUMBER");
            tmp = _content.substring(0, index).trim();
            setValue("phoneNo",tmp);

            _content = _content.substring(index).replace("VISA NUMBER", "").trim();
            index = _content.indexOf("SKILLS");
            tmp = _content.substring(0, index).trim();
            setValue("visaNo",tmp);

            _content = _content.substring(_content.indexOf("PASSPORT NUMBER")).replace("PASSPORT NUMBER", "").trim();
            index = _content.indexOf("PASSPORT ISSUE PLACE");
            tmp = _content.substring(0, index).trim();
            setValue("passportNo",tmp);

            _content = _content.substring(index).replace("PASSPORT ISSUE PLACE", "").trim();
            index = _content.indexOf("PASSPORT ISSUE DATE");
            tmp = _content.substring(0, index).trim();
            setValue("placeOfIssue",tmp);

            _content = _content.substring(index).replace("PASSPORT ISSUE DATE", "").trim();
            index = _content.indexOf("PASSPORT EXPIRY DATE");
            tmp = _content.substring(0, index).trim();
            setValue("pasportIssuedDate", new Date(tmp));
            _content = _content.substring(index).replace("PASSPORT EXPIRY DATE", "").trim();
            
            index = _content.indexOf("ADDRESS");
            tmp = _content.substring(0, index).trim();
            setValue("pasportExpiryDate", new Date(tmp));

            index = _content.indexOf("CITY");
            _content = _content.substring(index).replace("CITY", "").trim();
            
            index = _content.indexOf("ADDRESS");
            tmp = _content.substring(0, index).trim();
            setValue("placeOfBirth",tmp);
            setValue("city",tmp);

            _content = _content.substring(index).replace("ADDRESS", "").trim();
            index = _content.indexOf("RELATIVE CONTACT NECESSITY");
            tmp = _content.substring(0, index).trim();
            setValue("subcity",tmp);

            _content = _content.substring(_content.indexOf("RELATIVE NAME")).replace("RELATIVE NAME", "").trim();
            index = _content.indexOf("RELATIVE KINSHIP");
            tmp = _content.substring(0, index).trim();
            setValue("contactPerson",tmp);

            _content = _content.substring(index).replace("RELATIVE KINSHIP", "").trim();
            index = _content.indexOf("RELATIVE PHONE");
            tmp = _content.substring(0, index).trim();
            setValue("contactKinship",tmp);

            _content = _content.substring(index).replace("RELATIVE PHONE", "").trim();
            index = _content.indexOf("RELATIVE ADDRESS");
            tmp = _content.substring(0, index).trim();
            setValue("contactPhone",tmp);

            _content = _content.substring(index).replace("RELATIVE ADDRESS", "").trim();
            if (_content.includes("EMPLOYMENT DETAILS")) {
                index = _content.indexOf("EMPLOYMENT DETAILS");
                tmp = _content.substring(0, index).trim();
                setValue("contactAddress",tmp);
            } else {
                setValue("contactAddress",_content);
            }
        }

        else if (pastedContent.toUpperCase().includes("SURNAME") &&
            pastedContent.toUpperCase().includes("GIVEN NAMES")){
            let _content = pastedContent.toUpperCase().trim();
            let tmp = "";
            let index = _content.indexOf("SURNAME");
            _content = _content.substring(index).trim();
            _content = _content.replace("SURNAME", "").trim();

            index = _content.indexOf("GIVEN NAMES");
            tmp = _content.substring(0, index).trim();
            _content = _content.substring(index).replace("GIVEN NAMES", "").trim();

            index = _content.indexOf("DATE OF BIRTH");
            setValue("fullName",(_content.substring(0, index).trim() + " " + tmp));
            _content = _content.substring(index).replace("DATE OF BIRTH", "").trim();
            
            index = _content.indexOf("GENDER");
            tmp = _content.substring(0, index).trim();
            setValue("dateOfBirth",new Date(tmp));
            _content = _content.substring(index).replace("GENDER", "").trim();
            index = _content.indexOf("MARITAL STATUS");
            tmp = _content.substring(0, index).trim();
            setValue("gender",tmp.toUpperCase()==="MALE"?"Male":"Female");
            _content = _content.substring(index).replace("MARITAL STATUS", "").trim();
            
            index = _content.indexOf("RELIGION");
            tmp = _content.substring(0, index).trim();
            setValue("maritalStatus",tmp.charAt(0) + tmp.slice(1).toLowerCase());
            _content = _content.substring(index).replace("RELIGION", "").trim();
          
            index = _content.indexOf("JOB");
            tmp = _content.substring(0, index).trim();
            setValue("religion",(tmp.toLocaleUpperCase() === "NON MUSLIM" ? "Christian" : "Islam"));
            _content = _content.substring(index).replace("JOB", "").trim();
          
            index = _content.indexOf("EDUCATION LEVEL");
            tmp = _content.substring(0, index).trim();
            setValue("occupation",tmp.toLocaleUpperCase());
            _content = _content.substring(index).replace("EDUCATION LEVEL", "").trim();
            
            index = _content.indexOf("ID NUMBER");
            tmp = _content.substring(0, index).trim();
            setValue("qualification",tmp);
            _content = _content.substring(index).replace("ID NUMBER", "").trim();

            
            _content = _content.replace("MOBILE NUMBER", "PHONE NUMBER");
            _content = _content.substring(_content.indexOf("PHONE NUMBER")).replace("PHONE NUMBER", "").trim();
            index = _content.indexOf("VISA NUMBER");
            tmp = _content.substring(0, index).trim();
            setValue("phoneNo",tmp);

            _content = _content.substring(index).replace("VISA NUMBER", "").trim();
            index = _content.indexOf("SKILLS");
            tmp = _content.substring(0, index).trim();
            setValue("visaNo",tmp);

            _content = _content.substring(_content.indexOf("PASSPORT NUMBER")).replace("PASSPORT NUMBER", "").trim();
            index = _content.indexOf("PASSPORT ISSUE PLACE");
            tmp = _content.substring(0, index).trim();
            setValue("passportNo",tmp);

            _content = _content.substring(index).replace("PASSPORT ISSUE PLACE", "").trim();
            index = _content.indexOf("PASSPORT ISSUE DATE");
            tmp = _content.substring(0, index).trim();
            setValue("placeOfIssue",tmp);

            _content = _content.substring(index).replace("PASSPORT ISSUE DATE", "").trim();
            index = _content.indexOf("PASSPORT EXPIRY DATE");
            tmp = _content.substring(0, index).trim();
            setValue("pasportIssuedDate", new Date(tmp));
            _content = _content.substring(index).replace("PASSPORT EXPIRY DATE", "").trim();
            
            index = _content.indexOf("ADDRESS");
            tmp = _content.substring(0, index).trim();
            setValue("pasportExpiryDate", new Date(tmp));

            index = _content.indexOf("CITY");
            _content = _content.substring(index).replace("CITY", "").trim();
            
            index = _content.indexOf("ADDRESS");
            tmp = _content.substring(0, index).trim();
            setValue("placeOfBirth",tmp);
            setValue("city",tmp);

            _content = _content.substring(index).replace("ADDRESS", "").trim();
            index = _content.indexOf("RELATIVE CONTACT NECESSITY");
            tmp = _content.substring(0, index).trim();
            setValue("subcity",tmp);

            _content = _content.substring(_content.indexOf("RELATIVE NAME")).replace("RELATIVE NAME", "").trim();
            index = _content.indexOf("RELATIVE KINSHIP");
            tmp = _content.substring(0, index).trim();
            setValue("contactPerson",tmp);

            _content = _content.substring(index).replace("RELATIVE KINSHIP", "").trim();
            index = _content.indexOf("RELATIVE PHONE");
            tmp = _content.substring(0, index).trim();
            setValue("contactKinship",tmp);

            _content = _content.substring(index).replace("RELATIVE PHONE", "").trim();
            index = _content.indexOf("RELATIVE ADDRESS");
            tmp = _content.substring(0, index).trim();
            setValue("contactPhone",tmp);

            _content = _content.substring(index).replace("RELATIVE ADDRESS", "").trim();
            if (_content.includes("EMPLOYMENT DETAILS")) {
                index = _content.indexOf("EMPLOYMENT DETAILS");
                tmp = _content.substring(0, index).trim();
                setValue("contactAddress",tmp);
            } else {
                setValue("contactAddress",_content);
            }
            }
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [applicant, setValue, userSettings, filesLoaded, loadFilePreviews]);

  // Handle file input changes and generate previews
  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    
    // Clear any existing preview URL to prevent memory leaks
    if (previews[key]) {
      URL.revokeObjectURL(previews[key]);
    }
    
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [key]: previewURL }));
      setImageLoading(prev => ({ ...prev, [key]: false }));
      
      // Set the form value
      setValue(key, e.target.files);
      clearErrors(key);
    } else {
      setPreviews(prev => ({ ...prev, [key]: null }));
      setValue(key, null);
    }
  };

  // Scroll to photoFile on error
  useEffect(() => {
    if (errors.photoFile) {
      setSelectedTab("photoFile");
      setTimeout(() => {
        photoFileRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  }, [errors.photoFile]);

  // Handle form submission
  const handleFormSubmit = async (data) => {    
    if (!userSettings) {
      console.log("Loading user settings...");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Clean and format the data for server
      const cleanedData = {
        ...data,
        // Fix number fields - convert NaN to null
        experienceInAbroad: data.experienceInAbroad && !isNaN(data.experienceInAbroad) ? 
          Number(data.experienceInAbroad) : null,
        childrens: data.childrens && !isNaN(data.childrens) ? 
          Number(data.childrens) : null,
        salary: data.salary && !isNaN(data.salary) ? 
          Number(data.salary) : null,
        
        // Format dates
        dateOfBirth: formatDate(data.dateOfBirth),
        pasportIssuedDate: formatDate(data.pasportIssuedDate),
        pasportExpiryDate: formatDate(data.pasportExpiryDate),
        applicationDate: formatDate(data.applicationDate),
        beginningOfContract: formatDate(data.beginningOfContract),
        cocCertifiedDate: formatDate(data.cocCertifiedDate),
        contactBirthDate: formatDate(data.contactBirthDate),
        
        // Convert empty strings to null for better server handling
        currentNationality: data.currentNationality || "Ethiopia",
        sponsorID: data.sponsorID || null,
        visaNo: data.visaNo || null,
        applicationNo: data.applicationNo || null,
        phoneNo: data.phoneNo || null,
        sponsorPhone: data.sponsorPhone || null,
        sponsorName: data.sponsorName || null,
        qualification: data.qualification || null,
        city: data.city || null,
        officeAbroad: data.officeAbroad || null,
        nationalID: data.nationalID || null,
        sponsorNameArabic: data.sponsorNameArabic || null,
        sponsorEmail: data.sponsorEmail || null,
        english: data.english || null,
        arabic: data.arabic || null,
        workingCountry: data.workingCountry || null,
        height: data.height || null,
        weight_: data.weight_ || null,
        referenceNo: data.referenceNo || null,
        memo: data.memo || null,
        cooking: data.cooking || null,
      };

      const formData = new FormData();
      
      // Add all non-file form fields first
      Object.keys(cleanedData).forEach((key) => {
        // Skip file fields and undefined/null values
        if (!key.endsWith('File') && cleanedData[key] !== null && cleanedData[key] !== undefined) {
          formData.append(key, cleanedData[key]);
        }
      });

      // Add files separately - handle existing vs new files
      const fileFields = [
        "photoFile", "fullSizeFile", "passportFile", "interviewFile", "cocFile", "medicalFile", 
        "idCardFile", "relativeIdFile", "insuranceFile", "regFormFile"
      ];

      fileFields.forEach((field) => {
        // Check if a new file was selected (FileList has items)
        if (data[field] && data[field] instanceof FileList && data[field].length > 0) {
          // A new file was selected, upload it
          formData.append(field, data[field][0]);
        } 
        // No new file selected, check if there's an existing file to keep
        else if (applicant && applicant[`${field.replace('File', 'Key')}`]) {
          // Keep existing file key
          formData.append(`${field.replace('File', 'Key')}`, applicant[`${field.replace('File', 'Key')}`]);
        }
      });

      // First, create or update the main applicant record
      let applicantResponse;
      if (applicantId) {
        applicantResponse = await updateApplicant(applicantId, formData);
      } else {
        applicantResponse = await createApplicant(formData);
      }
      
      setIsSaving(false);
      onSuccess();
      
      if (applicant && applicant.visaNo !== "" && applicant.applicationNo !== "") {
        navigate(`/reports/applicantreport/${applicant.id}`);
      } else {
        navigate("/applicants/applicantlist");
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setIsSaving(false);
      
      // Enhanced error handling with detailed validation messages
      if (err.response?.data) {
        console.error("Server response:", err.response.data);
        
        if (err.response.data.errors) {
          // Format validation errors in a user-friendly way
          const validationErrors = Object.entries(err.response.data.errors)
            .map(([field, errors]) => {
              const fieldName = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
              return `${fieldName}: ${Array.isArray(errors) ? errors.join(', ') : errors}`;
            })
            .join('\n');
          
          setSaveError(`Please fix the following errors:\n${validationErrors}`);
        } else if (err.response.data.message) {
          setSaveError(err.response.data.message);
        } else if (typeof err.response.data === 'string') {
          setSaveError(err.response.data);
        } else {
          setSaveError("An error occurred while saving. Please check your data and try again.");
        }
      } else if (err.request) {
        setSaveError("Network error. Please check your connection and try again.");
      } else {
        setSaveError(err.message || "An unexpected error occurred. Please try again.");
      }
    }
  };

  if (loading) {
    return <LoadPanel visible={true} />;
  }

  return (
    <div className="container mt-0" style={{ maxWidth: "1100px" }}>     
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div 
          style={{ 
            position: 'sticky',
            top: 77,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: "40px",
            background: 'white',
            zIndex: 100,
            borderBottom: '1px solid #eee',
            gap: '40px',
          }}
        >
          <h5 className="es-label-title" style={{ paddingTop: '15px' }}>
            {title}
          </h5>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              type="submit" 
              className="es-toolbar-button"
              disabled={!companyIsActive || isSaving}
              style={{
                width: '35px',
                height: '35px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
              }}
              title="Save"
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : companyIsActive ? (
                <FaSave size={20} />
              ) : (
                <FaSave size={20} style={{ opacity: 0.5, cursor: 'not-allowed' }} />
              )}            
            </button>

            <a
              href="https://www.remove.bg/"
              target="_blank"
              rel="noopener noreferrer"
              className="es-toolbar-button"
              style={{
                width: '35px',
                height: '35px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                textDecoration: 'none',
                color: 'inherit'
              }}
              title="Remove Background"
            >
              <FaExternalLinkAlt size={20} />
            </a>

            <a
              href="https://tawtheeq.musaned.com.sa/"
              target="_blank"
              rel="noopener noreferrer"
              className="es-toolbar-button"
              style={{
                width: '35px',
                height: '35px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                textDecoration: 'none',
                color: 'inherit'
              }}
              title="Musaned"
            >
              <img src="/musaned24.png" alt="Musaned" style={{ width: '20px', height: '20px' }} />
            </a>
            
            {toBeExpired && (
              <span style={{ color: "red", fontWeight: "bold" }}>
                License update needed, Call 0911454176!
              </span>
            )}
          </div>
        </div>
        
        <div className="card p-3">
          <LoadPanel
            shadingColor="rgba(0,0,0,0.4)"
            position={{ of: '.dx-datagrid' }}
            visible={loading}
            showIndicator={true}
            shading={false}
            showPane={true}
          />

          <div className="mb-2">
          <Tab.Container activeKey={selectedTab} onSelect={(key) => setSelectedTab(key)}>
            <Nav variant="tabs" className="mb-1 es-tab-title">
              {[
                { key: "photoFile", label: "Photo" },
                { key: "fullSizeFile", label: "Full Size" },
                { key: "passportFile", label: "Passport" },
                { key: "interviewFile", label: "Video Interview" },
                // Only show premium tabs if companyIsPremium is true
                ...(companyIsPremium ? [
                  { key: "cocFile", label: "COC" },
                  { key: "medicalFile", label: "Medical" },
                  { key: "idCardFile", label: "ID Card" },
                  { key: "relativeIdFile", label: "Relative ID" },
                  { key: "insuranceFile", label: "Insurance" },
                  { key: "regFormFile", label: "Reg Form" },
                ] : [])
              ].map(({ key, label }) => (
                <Nav.Item key={key}>
                  <Nav.Link eventKey={key} className="mb-1 es-tab-title">{label}</Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            
            <Tab.Content>
              {[
                { key: "photoFile", label: "Upload Photo:", type: "image" },
                { key: "fullSizeFile", label: "Upload Full Size:", type: "image" },
                { key: "passportFile", label: "Upload Passport:", type: "image" },
                { key: "interviewFile", label: "Upload Video:", type: "video" },
                // Only show premium tabs if companyIsPremium is true
                ...(companyIsPremium ? [
                  { key: "cocFile", label: "Upload COC:", type: "image" },
                  { key: "medicalFile", label: "Upload Medical:", type: "image" },
                  { key: "idCardFile", label: "Upload ID Card:", type: "image" },
                  { key: "relativeIdFile", label: "Upload Relative ID:", type: "image" },
                  { key: "insuranceFile", label: "Upload Insurance:", type: "image" },
                  { key: "regFormFile", label: "Upload Reg Form:", type: "image" },
                ] : [])
              ].map(({ key, label, type }) => (
                <Tab.Pane eventKey={key} key={key}>
                    <div className="mb-2 text-center">
                      {/* MRZ Scan Button - Only show for passportFile when there's an uploaded file */}
                      {key === "passportFile" && previews.passportFile && title ==="CREATE APPLICANT" && (
                        <div className="mb-3">
                          <button
                            type="button"
                            onClick={handleScanMrzFromUploaded}
                            disabled={isScanningMrz || !companyIsActive}
                            className="btn btn-primary mb-2"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              margin: '0 auto',
                              padding: '8px 16px',
                              borderRadius: '4px'
                            }}
                          >
                            {isScanningMrz ? (
                              <>
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                Scanning Passport...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-qr-code"></i>
                                Scan Passport from Uploaded Image
                              </>
                            )}
                          </button>
                          
                          {/* Quality Messages */}
                          {mrzSuccess && (
                            <div className="alert alert-success mt-2" role="alert" style={{ fontSize: '0.9rem' }}>
                              <i className="bi bi-check-circle me-2"></i>
                              Passport scanned! Please check and correct the auto-filled information before saving.
                            </div>
                          )}
                          
                          {mrzError && mrzError.includes('Some fields may need verification') && (
                            <div className="alert alert-warning mt-2" role="alert" style={{ fontSize: '0.9rem' }}>
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              {mrzError}
                            </div>
                          )}
                          
                          {mrzError && !mrzError.includes('Some fields may need verification') && (
                            <div className="alert alert-danger mt-2" role="alert" style={{ fontSize: '0.9rem' }}>
                              <i className="bi bi-x-circle me-2"></i>
                              {mrzError}
                            </div>
                          )}
                          
                          <hr className="my-3" />
                        </div>
                      )}

                      <div style={previewBoxStyle}>
                        {previews[key] ? (
                          type === "video" ? (
                            // Video player for interview files
                            <video 
                              controls 
                              style={imageStyle}
                              key={previews[key]}
                              onLoadStart={() => setImageLoading(prev => ({...prev, [key]: false}))}
                              onError={(e) => {
                                console.error(`Failed to load video for ${key}`);
                                e.target.style.display = 'none';
                                setImageLoading(prev => ({...prev, [key]: false}));
                              }}
                            >
                              <source src={previews[key]} type="video/mp4" />
                              <source src={previews[key]} type="video/avi" />
                              <source src={previews[key]} type="video/quicktime" />
                              <source src={previews[key]} type="video/x-ms-wmv" />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            // Image preview for other files
                            <img 
                              src={previews[key]} 
                              alt={`${key} Preview`} 
                              style={imageStyle}
                              key={previews[key]}
                              onLoad={() => setImageLoading(prev => ({...prev, [key]: false}))}
                              onError={(e) => {
                                console.error(`Failed to load image for ${key}`);
                                e.target.style.display = 'none';
                                setImageLoading(prev => ({...prev, [key]: false}));
                              }}
                            />
                          )
                        ) : imageLoading[key] ? (
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : key === "interviewFile" && !allowVideo ? (
                          // Static advertisement video when video upload is not allowed
                          <div className="text-center w-100">
                            <div className="text-muted mb-2">Advanced feature to send CV with video interview</div>
                            <div 
                              style={{
                                ...imageStyle,
                                width: '100%',
                                maxWidth: '600px',
                                height: 'auto',
                                backgroundColor: '#f8f9fa',
                                border: '2px solid #dee2e6',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                              onClick={() => window.open('/sample.mp4', '_blank')}
                              title="Click to play video in new tab"
                            >
                              <img 
                                src="/contents/advertisement-poster.jpg" 
                                alt="See how to..."
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: '400px',
                                  objectFit: 'contain'
                                }}
                              />
                              <div 
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  borderRadius: '50%',
                                  width: '60px',
                                  height: '60px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <i className="fas fa-play text-white" style={{ fontSize: '24px', marginLeft: '4px' }}></i>
                              </div>
                            </div>
                          </div>
                        ) : type === "video" ? (
                          <div className="text-muted">No {key === "interviewFile" ? "video" : "file"} uploaded</div>
                        ) : (
                          <div className="text-muted">No {key.replace('File', '').toLowerCase()} uploaded</div>
                        )}
                      </div>
                      
                      {/* Only show file input for interview file when video upload is allowed */}
                      {!(key === "interviewFile" && !allowVideo) && (
                        <div className="d-flex align-items-center justify-content-center mt-3">
                          <label style={{ width: "250px" }} className="me-2 es-field-label-black">
                            {label}
                          </label>
                          <input
                            type="file"
                            className="form-control es-field-label-black"
                            {...register(key)}
                            accept={type === "video" ? "video/*" : "image/*"}
                            ref={key === "photoFile" ? photoFileRef : null}
                            onChange={(e) => handleFileChange(e, key)}
                          />
                        </div>
                      )}
                      
                      {errors[key] && (
                        <p className="text-danger mt-2">
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {errors[key].message}
                        </p>
                      )}
                    </div>
                  </Tab.Pane>
              ))}
            </Tab.Content>
          </Tab.Container>
        </div>

      <fieldset>
          <legend class="es-legend"></legend>
      {/* Form Fields - Two per row */}
      <div className="row mb-0">
        <div className="col-md-6">
        <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Application No. </label>
          <div className="col-sm-8 d-flex align-items-center">
              <input {...register("applicationNo")} className="form-control" />
              {errors.applicationNo && (<span className="text-danger ms-2">{errors.applicationNo.message} </span> )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label es-field-label">Date</label>
            <div className="col-sm-8">
                <DateBox
                    value={watch("applicationDate")}
                    displayFormat="dd/MM/yyyy" // Ensures proper date format
                    placeholder="DD/MM/YYYY"  // Watermark
                    onValueChanged={(e) => setValue("applicationDate", e.value)}
                    min={new Date("2000-01-01")}
                    width={"100%"}
                />
            </div>
          </div>
        </div>
      </div>
</fieldset>
      <div className="row mb-0">
        <div className="col-md-6">
          

        <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Full Name</label>
          <div className="col-sm-8 d-flex align-items-center">
            <input type="text" {...register("fullName")} className="form-control" disabled={!companyIsActive} />
            {errors.fullName && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
          </div>
        </div>

        </div>
        <div className="col-md-6">
          <div className="row">      
            <div className="form-check">
  <input 
    type="checkbox" 
    {...register("is_Active")} 
    className="form-check-input" 
    id="is_Active" 
  />
  <label className="form-check-label es-field-label" htmlFor="is_Active">Active ?</label>
</div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Passport No.</label>
          <div className="col-sm-8 d-flex align-items-center">
              <input {...register("passportNo")} className="form-control" disabled={!!applicant || !companyIsActive } />
              {errors.passportNo && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label  es-field-label">Passport Type</label>
            <div className="col-sm-8 d-flex align-items-center">
              <select {...register("passportType")} className="form-control">
                <option value=""></option>
                <option value="Normal">Normal</option>
                <option value="Diplomatic">Diplomatic</option>
                <option value="Special">Special</option>
                <option value="Haji Cert">Haji Cert</option>
                <option value="Travel Cert">Travel Cert</option>
              </select>
              {errors.passportType && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label es-field-label">Date of Birth</label>
            <div className="col-sm-8 d-flex align-items-center">
                  <DateBox
                      className="es-dx-field"
                      value={watch("dateOfBirth")}
                      displayFormat="dd/MM/yyyy" // Ensures proper date format
                      placeholder="DD/MM/YYYY"  // Watermark
                      onValueChanged={(e) => setValue("dateOfBirth", e.value)}
                      min={new Date("1900-01-01")}
                      width={"100%"}
                  />
                {errors.dateOfBirth && (errors.dateOfBirth.message === "Required" ? 
                  (<i className="bi bi-exclamation-circle text-danger ms-2"></i> ) : 
                  (<span className="text-danger">{errors.dateOfBirth.message} </span> )
                )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Place of Birth</label>
          <div className="col-sm-8 d-flex align-items-center">
              <input {...register("placeOfBirth")} className="form-control" />
              {errors.placeOfBirth && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label  es-field-label">Date of Issue</label>
            <div className="col-sm-8 d-flex align-items-center">
                <DateBox
                    value={watch("pasportIssuedDate")}
                    displayFormat="dd/MM/yyyy" // Ensures proper date format
                    placeholder="DD/MM/YYYY"  // Watermark
                    onValueChanged={(e) => {
                      setValue("pasportIssuedDate", e.value);
                      
                      // If there's a new date value, calculate and set expiry date
                      if (e.value) {
                        const issuedDate = new Date(e.value);
                        const expiryDate = new Date(issuedDate);
                        const passNo = getValues("passportNo");

                        const firstChar = passNo.substring(0, 1).toUpperCase();
                        const secondChar = passNo.substring(1, 2);

                        const birthDate = new Date(getValues("dateOfBirth"));
                        const today = new Date();
                        let age = today.getFullYear() - birthDate.getFullYear();
                        const monthDiff = today.getMonth() - birthDate.getMonth();
                        
                        // Adjust age if birthday hasn't occurred yet this year
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                          age--;
                        }
                        
                        if (firstChar === 'E' && !isNaN(secondChar) && secondChar !== ' ' && age > 25) {
                          expiryDate.setDate(expiryDate.getDate() + 3651);
                        }
                        else {// (firstTwoChars === 'EP' || firstTwoChars === 'EQ') {
                          expiryDate.setDate(expiryDate.getDate() + 1825); // Add 1825 days
                        } 
                        
                        setValue("pasportExpiryDate", expiryDate);
                      }
                    }}
                    min={new Date("2000-01-01")}
                    width={"100%"}
                />

              {errors.pasportIssuedDate && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label  es-field-label">Date of Expiry</label>
            <div className="col-sm-8 d-flex align-items-center">
                <DateBox
                    value={watch("pasportExpiryDate")}
                    displayFormat="dd/MM/yyyy" // Ensures proper date format
                    placeholder="DD/MM/YYYY"  // Watermark
                    onValueChanged={(e) => setValue("pasportExpiryDate", e.value)}
                    min={new Date("2000-01-01")}
                    width={"100%"}
                />
              {errors.pasportExpiryDate && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Place of Issue</label>
          <div className="col-sm-8 d-flex align-items-center">
              <input {...register("placeOfIssue")} className="form-control" />
              {errors.placeOfIssue && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Phone No.</label>
            <div className="col-sm-8">
              <input {...register("phoneNo")} className="form-control" />
            </div>
          </div>
        </div>
      </div>
      <fieldset>
          <legend class="es-legend"></legend>
      
      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Religion</label>
          <div className="col-sm-8 d-flex align-items-center">
              <select {...register("religion")} className="form-control">
                <option value=""></option>
                <option value="Islam">Islam</option>
                <option value="Christian">Christian</option>
                <option value="Orthodox">Orthodox</option>
                <option value="Catholic">Catholic</option>
                <option value="Protestant">Protestant</option>
                <option value="Buddhism">Buddhism</option>
                <option value="Hinduism">Hinduism</option>
                <option value="Sikhism">Sikhism</option>
                <option value="Non-Muslim">Non-Muslim</option>
              </select>
              {errors.religion && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label  es-field-label">Marital Status</label>
            <div className="col-sm-8 d-flex align-items-center">
              <select {...register("maritalStatus")} className="form-control">
                <option value=""></option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widow">Widow</option>
                <option value="Other">Other</option>
              </select>
              {errors.maritalStatus && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Gender</label>
          <div className="col-sm-8 d-flex align-items-center">
              <select {...register("gender")} className="form-control">
                <option value=""></option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
              {errors.gender && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label es-field-label">Occupation</label>
            <div className="col-sm-8 d-flex align-items-center">
              <select {...register("occupation")} className="form-control">
                <option value=""></option>
                <option value="HOUSE WORKER">HOUSE WORKER</option>
                <option value="HOUSE MAID">HOUSE MAID</option>
                <option value="NANNY">NANNY</option>
                <option value="HAIR DRESSER">HAIR DRESSER</option>
                <option value="FITNESS TRAINER">FITNESS TRAINER</option>
                <option value="BARESTA">BARESTA</option>
                <option value="MAKEUP ARTIST">MAKE UP ARTIST</option>
                <option value="FEMLAE NURSE">FEMLAE NURSE</option>
                <option value="FEMALE COOKER">FEMALE COOKER</option>
                <option value="FEMALE HOUSE MANAGER">FEMALE COOKER</option>
                <option value="FEMALE PHYSIOTHERAPIST">FEMALE PHYSIOTHERAPIST</option>
                <option value="DRIVER">DRIVER</option>
                <option value="MALE DRIVER">MALE DRIVER</option>
                <option value="PERSONAL DRIVER">PERSONAL DRIVER</option>
                <option value="MALE COOKER">MALE COOKER</option>
                <option value="MALE HOUSE FARMER">MALE HOUSE FARMER</option>
                <option value="MALE HOUSE JANITOR">MALE HOUSE JANITOR</option>
                <option value="MALE NURSE">MALE NURSE</option>
                <option value="MALE PERSONAL HELPER">MALE PERSONAL HELPER</option>
                <option value="COOK/WAITER">COOK/WAITER</option>
                <option value="MALE PHYSIOTHERAPIST">MALE PHYSIOTHERAPIST</option>
                <option value="SALES REPRESENTATIVE">SALES REPRESENTATIVE</option>
                <option value="OTHER">OTHER</option>
              </select>
              {errors.occupation && (<i className="bi bi-exclamation-circle text-danger ms-2"></i>)}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Qualification</label>
            <div className="col-sm-8">
              <input {...register("qualification")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
            <label className="col-sm-4 col-form-label es-field-label">City</label>
            <div className="col-sm-8">
              <input {...register("city")} className="form-control" />
            </div>
          </div>
        </div>
      </div>
</fieldset>
<fieldset>
    <legend class="es-legend es-label-title"> SPONSOR & VISA INFORMATION</legend>
    <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Visa Number</label>
          <div className="col-sm-8 d-flex align-items-center">
              <input {...register("visaNo")} className="form-control" />
              {errors.visaNo && (<span className="text-danger ms-2">{errors.visaNo.message} </span> )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label-blue">Sponsor Name</label>
            <div className="col-sm-8">
              <input {...register("sponsorName")} className="form-control" />
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label  es-field-label">Sponsor ID</label>
          <div className="col-sm-8 d-flex align-items-center">
              <input {...register("sponsorID")} className="form-control" />
              {errors.sponsorID && (<span className="text-danger ms-2">{errors.sponsorID.message} </span> )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label-blue">Sponsor Phone</label>
            <div className="col-sm-8">
              <input {...register("sponsorPhone")} className="form-control" />
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label-blue">Sponsor Address</label>
            <div className="col-sm-8">
              <input {...register("sponsorAddress")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
        <div className="row">
            <label className="col-sm-4 col-form-label es-field-label-blue">Agent</label>
            <div className="col-sm-8">
              <select {...register("officeAbroad")} className="form-control">
                <option value="">Select...</option>
                {agents && agents.map(company => (
                  <option key={company.companyID} value={company.companyID}>
                    {company.companyName}
                  </option>
                ))}
              </select>
            </div>
          </div>

        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">National ID</label>
            <div className="col-sm-8">
              <input {...register("nationalID")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Sponsor Arabic</label>
            <div className="col-sm-8">
              <input {...register("sponsorNameArabic")} className="form-control" />
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Email</label>
            <div className="col-sm-8">
              <input {...register("sponsorEmail")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Visa Type</label>
          <div className="col-sm-8">
              <select {...register("visaType")} className="form-control">
                <option value="Business Visit">Business Visit</option>
                <option value="Commercial Visit">Commercial Visit</option>
                <option value="Diplomatic">Diplomatic</option>
                <option value="Educational">Educational</option>
                <option value="Escort Visa">Escort Visa</option>
                <option value="Event Visa">Event Visa</option>
                <option value="Extend Return">Extend Return</option>
                <option value="Family Visit">Family Visit</option>
                <option value="Government Visit">Government Visit</option>
                <option value="Hajj">Hajj</option>
                <option value="Medical">Medical</option>
                <option value="Merchandise Delivery">Merchandise Delivery</option>
                <option value="Missions and Organizations Visit">Missions and Organizations Visit</option>
                <option value="Personal Visit">Personal Visit</option>
                <option value="Resident">Resident</option>
                <option value="Seasonal Work">Seasonal Work</option>
                <option value="Special">Special</option>
                <option value="Temporary work">Temporary work</option>
                <option value="Tourism">Tourism</option>
                <option value="Transit">Transit</option>
                <option value="Umrah">Umrah</option>
                <option value="Work">Work</option>
                <option value="Working Visit">Working Visit</option>
                <option value="Other">Other</option>
              </select>
          </div>
        </div>
      </div>
      </div>
</fieldset>

<CollapsiblePanel title={"Relative Information"}>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Relative Name</label>
            <div className="col-sm-8">
                <input {...register("contactPerson")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Address Region</label>
            <div className="col-sm-8">
              <input {...register("contactAddress")} className="form-control" />
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Relitave Phone</label>
            <div className="col-sm-8">
              <input {...register("contactPhone")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Relative Kinship</label>
            <div className="col-sm-8">
              <input {...register("contactKinship")} className="form-control" />
            </div>
          </div>
        </div>
      </div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">City</label>
            <div className="col-sm-8">
              <input {...register("contactCity")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Subcity/Zone</label>
          <div className="col-sm-8">
              <input {...register("contactSubCity")} className="form-control" />
            </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Relitave Woreda</label>
            <div className="col-sm-8">
              <input {...register("contactWereda")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
        <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Relative House.#</label>
          <div className="col-sm-8">
              <input {...register("contactHouseNo")} className="form-control" />
            </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Relative gender</label>
            <div className="col-sm-8">                  
            <select {...register("contactGender")} className="form-control">
                <option value=""></option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">R-Birth Date</label>
          <div className="col-sm-8">
                <DateBox
                    value={watch("contactBirthDate")}
                    displayFormat="dd/MM/yyyy" // Ensures proper date format
                    placeholder="DD/MM/YYYY"  // Watermark
                    onValueChanged={(e) => setValue("contactBirthDate", e.value)}
                    min={new Date("1900-01-01")}
                    width={"100%"}
                />
          </div>
        </div>
      </div>
</div>
</CollapsiblePanel>

<CollapsiblePanel title={"Other Information"}>

<div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Region</label>
          <div className="col-sm-8">
            <select {...register("region_")} className="form-control">
              <option value=""></option>
              <option value="Addis Ababa">Addis Ababa</option>
              <option value="Afar">Afar</option>
              <option value="Amhara">Amhara</option>
              <option value="Benishangul-Gumuz">Benishangul-Gumuz</option>
              <option value="Dire Dawa">Dire Dawa</option>
              <option value="Gambela">Gambela</option>
              <option value="Harari">Harari</option>
              <option value="Oromia">Oromia</option>
              <option value="Sidama">Sidama</option>
              <option value="Somalia">Somalia</option>
              <option value="Tigray">Tigray</option>
              <option value="South West Ethiopia">South West Ethiopia</option>
              <option value="SNNP">SNNP</option>
            </select>
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Subcity</label>
          <div className="col-sm-8">
            <input {...register("subcity")} className="form-control" />
          </div>
        </div>
      </div>
    </div>

    <div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Woreda</label>
          <div className="col-sm-8">
            <input {...register("kebele")} className="form-control" />
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">House No.</label>
          <div className="col-sm-8">
            <input {...register("houseNo")} className="form-control" />
          </div>
        </div>
      </div>
    </div>
    <fieldset>
    <legend class="es-legend"></legend>
    </fieldset>      
<div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">File No.</label>
          <div className="col-sm-8">
            <input {...register("location")} className="form-control" />
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Contract #</label>
        <div className="col-sm-8">
            <input {...register("musanedNumber")} className="form-control" />
          </div>
      </div>
    </div>
</div>

<div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Wakala #</label>
          <div className="col-sm-8">
            <input {...register("wakalaNo")} className="form-control" />
          </div>
        </div>
      </div>
      <div className="col-md-6">
      <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Sticker Visa #</label>
        <div className="col-sm-8">
            <input {...register("stickerVisaNo")} className="form-control" />
          </div>
      </div>
    </div>
</div>

<div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Signed On</label>
          <div className="col-sm-8">
                <DateBox
                  value={watch("beginningOfContract")} 
                  onValueChanged={(e) => setValue("beginningOfContract", e.value)} 
                  min={new Date("2000-01-01")}
                  width={"100%"}
                />
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Nationality</label>
        <div className="col-sm-8">
          <select {...register("currentNationality")} className="form-control select-with-arrow">
              <option value="">Select Nationality</option>
              <option value="Ethiopia">Ethiopia</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
          </select>

          </div>
      </div>
    </div>
</div>

<div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Biometric ID</label>
          <div className="col-sm-8">
            <input {...register("biometricID")} className="form-control" />
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
        <label className="col-sm-4 col-form-label es-field-label">Labor ID</label>
        <div className="col-sm-8">
            <input {...register("laborID")} className="form-control" />
          </div>
      </div>
    </div>
</div>

<fieldset>
<legend class="es-legend"></legend>
<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Contact Person(2nd)</label>
            <div className="col-sm-8">
              <input {...register("brokerName")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
        <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Contact Phone(2nd)</label>
          <div className="col-sm-8">
              <input {...register("brokerPhone")} className="form-control" />
            </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Coc Center</label>
            <div className="col-sm-8">                  
                <input {...register("cocCenterName")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Certified Date</label>
          <div className="col-sm-8">
                <DateBox 
                  value={watch("cocCertifiedDate")} 
                  onValueChanged={(e) => setValue("cocCertifiedDate", e.value)} 
                  min={new Date("2000-01-01")}
                  width={"100%"}
                />
            </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Certificate No.</label>
            <div className="col-sm-8">                  
                <input {...register("cocCertificateNo")} className="form-control" />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Training Type</label>
          <div className="col-sm-8">
                <input {...register("cocCenter")} className="form-control" />
          </div>
        </div>
      </div>
</div>
<div className="row mb-0">
  <div className="col-md-2">
  </div>
  <div className="col-md-3">
      <div className="form-check">
        <input type="checkbox" {...register("isPhotoFiled")} className="form-check-input" id="isPhotoFiled" />
        <label className="form-check-label es-field-label" htmlFor="isPhotoFiled">Photos (2)</label>
      </div>
  </div>
  <div className="col-md-3">
      <div className="form-check">
        <input type="checkbox" {...register("isIDFiled")} className="form-check-input" id="isIDFiled" />
        <label className="form-check-label es-field-label" htmlFor="cocCenter">IsIDFiled</label>
      </div>
  </div>
  <div className="col-md-3">
      <div className="form-check">
        <input type="checkbox" {...register("isContactIDFiled")} className="form-check-input" id="isContactIDFiled" />
        <label className="form-check-label es-field-label" htmlFor="cocCenter">Relative ID Card</label>
      </div>
  </div>
</div>
</fieldset>  
</CollapsiblePanel>

<CollapsiblePanel title={"Skills & Experience"} defaultExpanded={true}>
<div className="row mb-0">
      <div className="col-md-6">
        <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">English</label>
          <div className="col-sm-8">
              <select {...register("english")} className="form-control">
                <option value="">Select...</option>
                <option value="POOR">POOR</option>
                <option value="FAIR">FAIR</option>
                <option value="GOOD">GOOD</option>
                <option value="YES">YES</option>
                <option value="NO">NO</option>
                <option value="10 %">10 %</option>
                <option value="20 %">20 %$</option>
                <option value="30 %">30 %</option>
                <option value="40 %">40 %</option>
                <option value="50 %">50 %</option>
                <option value="60 %">60 %</option>
                <option value="70 %">70 %</option>
                <option value="80 %">80 %</option>
                <option value="90 %">90 %</option>
                <option value="100 %">100 %</option>
              </select>
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Arabic</label>
          <div className="col-sm-8">
              <select {...register("arabic")} className="form-control">
                <option value="">Select...</option>
                <option value="POOR">POOR</option>
                <option value="FAIR">FAIR</option>
                <option value="GOOD">GOOD</option>
                <option value="YES">YES</option>
                <option value="NO">NO</option>
                <option value="10 %">10 %</option>
                <option value="20 %">20 %$</option>
                <option value="30 %">30 %</option>
                <option value="40 %">40 %</option>
                <option value="50 %">50 %</option>
                <option value="60 %">60 %</option>
                <option value="70 %">70 %</option>
                <option value="80 %">80 %</option>
                <option value="90 %">90 %</option>
                <option value="100 %">100 %</option>
              </select>
          </div>
        </div>
      </div>
    </div>

    <div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Experience Abroad</label>
            <div className="col-sm-8">                  
            <input type="number" step="1" min="0" max="50" {...register("experienceInAbroad", 
              {valueAsNumber: true, validate: value => Number.isInteger(value) || "Value must be an integer"})} className="form-control"
            />
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Salary</label>
          <div className="col-sm-8">
          <input type="number" step="1" min="0" max="99999" {...register("salary", 
              {valueAsNumber: true, validate: value => Number.isInteger(value) || "Value must be an integer"})} className="form-control"
            />
          </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Works In</label>
            <div className="col-sm-8">                  
            <input {...register("workingCountry")} className="form-control" />                
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Children</label>
          <div className="col-sm-8">
              <input type="number" step="1" min="0" max="50" {...register("childrens", 
                {valueAsNumber: true, validate: value => Number.isInteger(value) || "Value must be an integer"})} className="form-control"
              />
          </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Height</label>
            <div className="col-sm-8">                  
            <input {...register("height")} className="form-control" />                
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Weight</label>
          <div className="col-sm-8">
          <input {...register("weight_")} className="form-control" />                
          </div>
        </div>
      </div>
</div>

<div className="row mb-0">
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Reference No.</label>
            <div className="col-sm-8">                  
            <input {...register("referenceNo")} className="form-control" />                
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="row">
          <label className="col-sm-4 col-form-label es-field-label">Remark</label>
          <div className="col-sm-8">
          <input {...register("memo")} className="form-control" />                
          </div>
        </div>
      </div>
</div>

<div className="row mb-0">
    <div className="col-md-1" />
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("ironing")} className="form-check-input" id="ironing" />
          <label className="form-check-label es-field-label" htmlFor="Ironing">Ironing</label>
        </div>
    </div>
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("sewing")} className="form-check-input" id="sewing" />
          <label className="form-check-label es-field-label" htmlFor="sewing">Sewing</label>
        </div>
    </div>
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("babySitting")} className="form-check-input" id="babySitting" />
          <label className="form-check-label es-field-label" htmlFor="babySitting">B.Sitting</label>
        </div>
    </div>
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("childCare")} className="form-check-input" id="childCare" />
          <label className="form-check-label es-field-label" htmlFor="childCare">Ch.Care</label>
        </div>
    </div>        
</div>

<div className="row mb-0">
    <div className="col-md-1" />
    
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("arabicCooking")} className="form-check-input" id="arabicCooking" />
          <label className="form-check-label es-field-label" htmlFor="arabicCooking">Ar.Cooking</label>
        </div>
    </div>
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("cleaning")} className="form-check-input" id="cleaning" />
          <label className="form-check-label es-field-label" htmlFor="cleaning">Cleaning</label>
        </div>
    </div>
    <div className="col-md-2">
        <div className="form-check">
          <input type="checkbox" {...register("washing")} className="form-check-input" id="washing" />
          <label className="form-check-label es-field-label" htmlFor="washing">Washing</label>
        </div>
    </div>

    <div className="col-md-4">
          <div className="row">
          <label className="col-sm-3 col-form-label es-field-label">Cooking</label>
            <div className="col-sm-8">
            <select {...register("cooking")} className="form-control">
                <option value=""></option>
                <option value="NO">NO</option>
                <option value="YES">YES</option>
                <option value="WILLING TO LEARN">WILLING TO LEARN</option>
              </select>
            </div>
          </div>
    </div>
</div>
</CollapsiblePanel>
      {/* Save button with loading state */}
      <div className="d-flex justify-content-end">
        <button
          type="submit"
          className="btn es-button"
          disabled={!companyIsActive || isSaving} // Disable if saving or inactive
          cursor="not-allowed"
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            "SAVE"
          )}
        </button>
      </div>

      {/* Display save error */}
      {saveError && (
        <div className="alert alert-danger mt-3" role="alert">
          {saveError}
        </div>
      )}
    
  </div>
  </form>
</div>
);
};

export default ApplicantForm;